import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip,
  Button, Alert, TextField, Grid, MenuItem, Stack, Stepper, Step, StepLabel,
  IconButton, Tooltip,
} from '@mui/material';
import { Gavel, Add, WarningAmber, CheckCircle, Edit, Delete } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmtMoney, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const MEASURES = [
  'Verbal Warning', 'First Written Warning', 'Second Written Warning', 'Final Written Warning',
  'Suspension with Pay', 'Salary Deduction', 'Termination', 'Dismissal',
];
const TERMINAL = ['Termination', 'Dismissal'];
const LADDER = ['Verbal Warning', 'First Written Warning', 'Second Written Warning', 'Final Written Warning', 'Termination'];
const STATUS_COLOR = { Open: 'warning', InProgress: 'info', Closed: 'success' };
const SEV_COLOR = { Low: 'default', Medium: 'info', High: 'warning', Critical: 'error' };

const DisciplinaryCard = ({ caseId, onClose, onChanged }) => {
  const [c, setC]   = useState(null);
  const [error, setError] = useState('');
  const [next, setNext]   = useState(null);
  const [form, setForm]   = useState(null);   // add-action form
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(form), value: form, onClose: () => setForm(null) });
  const [closing, setClosing] = useState(false);
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/disciplinary/cases/${caseId}`, { headers: hdrs() });
      setC(r.data);
      if (r.data.status !== 'Closed') {
        const n = await axios.get(`${API}/api/disciplinary/cases/${caseId}/next-measure`, { headers: hdrs() });
        setNext(n.data);
      }
    } catch (e) { setError(e.response?.data?.error || 'Failed to load'); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const saveAction = async () => {
    setBusy(true); setError('');
    const body = { measure: form.measure, notes: form.notes, approvedBy: form.approvedBy, amount: form.amount ? Number(form.amount) : undefined };
    try {
      if (form._id) await axios.put(`${API}/api/disciplinary/cases/${c._id}/action/${form._id}`, body, { headers: hdrs() });
      else          await axios.post(`${API}/api/disciplinary/cases/${c._id}/action`, body, { headers: hdrs() });
      setForm(null); refresh();
    } catch (e) { setError(e.response?.data?.error || 'Failed to save measure'); }
    finally { setBusy(false); }
  };

  const deleteAction = async (actionId) => {
    setBusy(true); setError('');
    try { await axios.delete(`${API}/api/disciplinary/cases/${c._id}/action/${actionId}`, { headers: hdrs() }); refresh(); }
    catch (e) { setError(e.response?.data?.error || 'Failed to delete measure'); }
    finally { setBusy(false); }
  };

  const closeCase = async (outcome) => {
    setBusy(true);
    try { await axios.post(`${API}/api/disciplinary/cases/${c._id}/close`, { outcome }, { headers: hdrs() }); setClosing(false); refresh(); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!c) return <Dialog open fullWidth maxWidth="md"><DialogContent>{error ? <Alert severity="error">{error}</Alert> : 'Loading…'}</DialogContent></Dialog>;

  const ladderStep = c.isGross ? -1 : LADDER.findIndex(m => m === c.currentStage);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle component="div">
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" component="div" fontWeight={700}>{c.employeeName} <Typography component="span" variant="body2" color="text.secondary">({c.employeeNo})</Typography></Typography>
            <Typography variant="caption" component="div" color="text.secondary">{c.caseNo} · {c.department || '—'} · Reported {fmtDate(c.dateReported)}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip label={c.offenceType} size="small" color={c.isGross ? 'error' : 'default'} />
            <Chip label={c.severity} size="small" color={SEV_COLOR[c.severity] || 'default'} variant="outlined" />
            <Chip label={c.status} size="small" color={STATUS_COLOR[c.status] || 'default'} />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {c.isGross && <Alert severity="error" icon={<WarningAmber />} sx={{ mb: 2 }}>Gross misconduct — does not follow the progressive ladder; may lead to direct dismissal (administrator approval required).</Alert>}
        {c.outcome && <Alert severity={['Terminated', 'Dismissed'].includes(c.outcome) ? 'error' : 'success'} sx={{ mb: 2 }}>Outcome: <strong>{c.outcome}</strong>{c.closedAt ? ` · closed ${fmtDate(c.closedAt)}` : ''}</Alert>}

        {/* Captured details (§4.7.4) */}
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Offence Date</Typography><Typography variant="body2">{fmtDate(c.offenceDate)}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Category</Typography><Typography variant="body2">{c.offenceCategory || '—'}</Typography></Grid>
          <Grid item xs={12}><Typography variant="caption" color="text.secondary">Description</Typography><Typography variant="body2">{c.description}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Witnesses</Typography><Typography variant="body2">{c.witnesses?.length ? c.witnesses.join(', ') : '—'}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Raised By</Typography><Typography variant="body2">{c.raisedBy || '—'}</Typography></Grid>
          {c.comments && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Comments</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{c.comments}</Typography></Grid>}
          {c.recommendations && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Recommendations</Typography><Typography variant="body2">{c.recommendations}</Typography></Grid>}
        </Grid>

        {/* Progressive ladder visual (non-gross) */}
        {!c.isGross && (
          <Stepper activeStep={ladderStep} alternativeLabel sx={{ mb: 2 }}>
            {LADDER.map(m => <Step key={m}><StepLabel>{m.replace(' Warning', '')}</StepLabel></Step>)}
          </Stepper>
        )}

        {/* Actions taken */}
        <Typography variant="subtitle1" fontWeight={700} mb={1}>Disciplinary Measures Taken</Typography>
        {c.actions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" mb={1}>No measures recorded yet.</Typography>
        ) : (
          <Stack spacing={1} mb={2}>
            {c.actions.map((a, i) => (
              <Box key={a._id || i} sx={{ border: '1px solid #e8eaf0', borderRadius: 2, p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                  <Typography variant="body2" fontWeight={700}>{a.measure}{a.amount ? ` — ${fmtMoney(a.amount)}` : ''}</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">{fmtDate(a.date)}</Typography>
                    {c.status !== 'Closed' && (
                      <>
                        <Tooltip title="Edit measure"><IconButton size="small" onClick={() => setForm({ _id: a._id, measure: a.measure, notes: a.notes || '', approvedBy: a.approvedBy || '', amount: a.amount || '' })}><Edit sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Delete measure"><IconButton size="small" color="error" onClick={() => deleteAction(a._id)}><Delete sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
                {a.notes && <Typography variant="caption" display="block">{a.notes}</Typography>}
                <Typography variant="caption" color="text.secondary">Issued by {a.issuedBy || '—'}{a.approvedBy ? ` · approved by ${a.approvedBy}` : ''}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2 }}>
        {c.status !== 'Closed' && <>
          <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ measure: next?.next || '', notes: '', approvedBy: '', amount: '' })}>Record Measure</Button>
          <Button variant="outlined" startIcon={<CheckCircle />} onClick={() => setClosing(true)}>Close Case</Button>
        </>}
        <Box flex={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Add-action dialog */}
      {discardDialog}
      <Dialog open={Boolean(form)} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Gavel color="primary" /> {form?._id ? 'Edit Disciplinary Measure' : 'Record Disciplinary Measure'}</DialogTitle>
        <DialogContent dividers>
          {form && (
            <Grid container spacing={2}>
              {next && !form._id && <Grid item xs={12}><Alert severity="info">Recommended next: <strong>{next.next}</strong> — {next.reason}</Alert></Grid>}
              <Grid item xs={12}>
                <TextField select label="Measure *" value={form.measure} onChange={e => setForm({ ...form, measure: e.target.value })} fullWidth size="small">
                  {MEASURES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              {form.measure === 'Salary Deduction' && (
                <Grid item xs={12}><TextField type="number" label={`Deduction Amount (${sym()})`} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} fullWidth size="small" /></Grid>
              )}
              {TERMINAL.includes(form.measure) && (
                <Grid item xs={12}><TextField label="Administrator Approval *" value={form.approvedBy} onChange={e => setForm({ ...form, approvedBy: e.target.value })} fullWidth size="small" placeholder="Name of approving administrator" helperText="Termination/Dismissal requires administrator approval" /></Grid>
              )}
              <Grid item xs={12}><TextField label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} fullWidth size="small" multiline rows={2} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" disabled={busy || !form?.measure} onClick={saveAction}>{form?._id ? 'Save' : 'Record'}</Button>
        </DialogActions>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={closing} onClose={() => setClosing(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close Case</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" mb={2}>Record the outcome of this disciplinary case.</Typography>
          <Stack spacing={1}>
            {['Resolved', 'Warning Issued', 'Suspended', 'Salary Deducted'].map(o => (
              <Button key={o} variant="outlined" onClick={() => closeCase(o)} disabled={busy}>{o}</Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setClosing(false)}>Cancel</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DisciplinaryCard;
